'use client';

import { Box, Dialog, DialogContent, DialogContentProps } from '@mui/material';
import { ReactNode } from 'react';

import { useGeneralDialogStepper } from '@/hooks/useGeneralDialogStepper';
import { GeneralDialogProvider } from '@/providers/GeneralDialogProvider';
import { DialogStepper, StepperStep } from './DialogStepper';
import { TopBar, TopBarProps } from './TopBar';

interface GeneralDialogSlotsContainerProps {
  slotsProps?: {
    topBar?: TopBarProps;
    dialogContent?: DialogContentProps;
  };
  slots?: {
    topBar?: typeof TopBar;
  };
  children: ReactNode;
  fullScreen?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

interface NewGeneralDialogWithStepperProps
  extends GeneralDialogSlotsContainerProps {
  steps: StepperStep[];
  initialActiveStepIndex?: number;
}

interface NewGeneralDialogWithoutStepperProps
  extends GeneralDialogSlotsContainerProps {
  steps?: never;
  initialActiveStepIndex?: never;
}

type NewGeneralDialogProps = (
  | NewGeneralDialogWithStepperProps
  | NewGeneralDialogWithoutStepperProps
) & {
  isOpen: boolean;
  onClose?: () => void;
};

export const NewGeneralDialog = (props: NewGeneralDialogProps) => {
  const {
    steps,
    initialActiveStepIndex,
    onClose,
    isOpen,
    children,
    slots,
    slotsProps,
    maxWidth,
    fullScreen = true,
  } = props;

  return (
    <Dialog
      fullScreen={fullScreen}
      maxWidth={maxWidth}
      fullWidth
      scroll="paper"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: 'background.default',
          borderRadius: fullScreen ? 0 : 1.5,
        },
      }}>
      {steps ? (
        <GeneralDialogProvider
          activeStepIndex={initialActiveStepIndex}
          steps={steps}>
          <GeneralDialogSlotsContainer
            slots={slots}
            fullScreen={fullScreen}
            slotsProps={{
              ...slotsProps,
              topBar: {
                onClose,
                ...slotsProps?.topBar,
              },
            }}>
            {children}
          </GeneralDialogSlotsContainer>
        </GeneralDialogProvider>
      ) : (
        <GeneralDialogSlotsContainer>{children}</GeneralDialogSlotsContainer>
      )}
    </Dialog>
  );
};

const TopBarContainer = (props: TopBarProps) => {
  const { isStepperVisible, steps, activeStepIndex } =
    useGeneralDialogStepper();

  return (
    <TopBar {...props}>
      {props.children ||
        (steps && isStepperVisible && (
          <Box
            maxWidth={({ spacing }) => spacing(120)}
            width="100%"
            sx={{
              overflowX: 'auto',
            }}>
            <DialogStepper
              steps={steps}
              activeStep={props?.activeStep || activeStepIndex}
            />
          </Box>
        ))}
    </TopBar>
  );
};

const GeneralDialogSlotsContainer = (
  props: GeneralDialogSlotsContainerProps
) => {
  const { slotsProps, children, slots, fullScreen } = props;
  const TopBarSlot = slots?.topBar || TopBarContainer;
  return (
    <>
      {fullScreen ? <TopBarSlot {...slotsProps?.topBar} /> : <></>}
      <DialogContent {...slotsProps?.dialogContent}>{children}</DialogContent>
    </>
  );
};
